/**
 * Created by Ning Jiang on 10/10/2018.
 */

import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {Timeline, TimelineLite} from "gsap";
import {FITHGameConfig} from "../../FITHGameConfig";
import {FITHInitialWinCountUp} from "./FITHInitialWinCountUp";
import {SequentiallyBetWayInitialWPController} from "@nolimitcity/slot-game/bin/game/winpresentation/initial/sequentially/SequentiallyBetWayInitialWPController";
import {FITHParsedGameData} from "../../server/data/FITHParsedGameData";
import {IBetWayWinData} from "@nolimitcity/slot-game/bin/core/server/data/IServerData";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {BetWinMode} from "@nolimitcity/slot-game/bin/core/gametype/BetWinMode";
import {StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {FITHGameModuleConfig} from "../../FITHGameModuleConfig";
import {ReelSymbolName} from "@nolimitcity/slot-game/bin/core/reelsymbol/ReelSymbolName";
import {FITHAvalancheReelController} from "../../reel/FITHAvalancheReelController";
import {FITHGameMode} from "../../gameMode/FITHGameMode";
import {FITHWaysWonGFXText} from "./FITHWaysWonGFXText";
import {FITHInitialWPView} from "./FITHInitialWPView";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {IWinRatioConfig, WinRatioType} from "@nolimitcity/slot-game/bin/core/winpresentation/WinRatio";
import {FITHSoundManager} from "../../sound/FITHSoundManager";

export class FITHInitialWPController extends SequentiallyBetWayInitialWPController {
    private readonly _view: FITHInitialWPView;
    private readonly _winCountUp: FITHInitialWinCountUp;
    private readonly _waysWon: FITHWaysWonGFXText;
    private _winWaysWon: number = 0;
    private _animationTimeline: TimelineLite | null;
    private _animationAborted: boolean = false;
    private _winNumberTimelines: (TimelineLite | null)[] = [];
    private _betlineHighlightSoundPlayed: boolean = false;
    private _isAborted: boolean = false;
    private _smallExplodeSoundPlayed: boolean = false;

    constructor(indexes: [number, number]) {
        super(indexes, {
            name: "InitialWin",
            betWinMode: BetWinMode.BET_WAY,
            hasWinCountUp: false,
            skippable: true
        });

        this._view = new FITHInitialWPView();

        this._winCountUp = new FITHInitialWinCountUp();
        this._winCountUp.position.set(360, 360);
        StageManager.getLayer(FITHGameConfig.instance.LAYERS.WIN_COUNT_UP.name).addChild(this._winCountUp);
        this._waysWon = FITHGameModuleConfig.waysWonGfx = new FITHWaysWonGFXText();
    }

    protected hasWin(): boolean {
        // #166 resetting it from here, since it will always be called.
        FITHGameConfig.instance.NEAR_WIN_LAST_SCATTER_REEL_ID = (FITHGameConfig.instance.REELS_NUM + 1);

        const data: FITHParsedGameData = (this._gameData as FITHParsedGameData);

        // #353 -> Since it is an avalanche game, we are checking total win on every round in main game.
        FITHGameConfig.instance.WIN_BELOW_STAKE = (SlotGame.api.gameClientConfiguration.belowStakeWinRestriction && (data.totalWin <= data.playedBetValue));

        return (data.mode !== FITHGameMode.BONUS)
            && (data.avalancheTotalWin > 0 || (data.nextMode === FITHGameMode.NORMAL_AVALANCHE && !(data.wildTransform)));
    }

    protected getNextSymbolAnimationTimeOffset(): number {
        return 0;
    }

    protected getNextBetWinAnimationTimeOffset(lastSymbolDelay: number): number {
        return 0.2;
    }

    protected startWinPresentation(): void {
        const timeline: TimelineLite = new TimelineLite();
        this._animationTimeline = new TimelineLite({paused: true});
        if (!(<FITHParsedGameData>this._gameData!).wildTransform) {
            if ((this._betWinsData !== null && this._betWinsData.length > 0) || (this._gameData!.nextMode === FITHGameMode.NORMAL_AVALANCHE)) {
                this.addWinAnimationsToTimeline(this._animationTimeline);
            } else {
                this.addWinCountUpToTimeline(this._animationTimeline);
            }
        }

        timeline.add(() => this._animationTimeline!.play());
        timeline.add(() => this.finish(), this._animationTimeline.totalDuration() + 0.1);
        this._timeline = timeline;
    }

    protected addWinAnimationsToTimeline(timeline: Timeline): void {
        let startTime: number = 0,
            wildFoundInExplode: boolean;

        const winRatioConfig: IWinRatioConfig = this._gameData!.currentWinRatioConfig!,
            allTogether: boolean = winRatioConfig.duration < 1,
            allWinPositions: boolean[][] = (<FITHParsedGameData>this._gameData!).allWinPositions,
            explodePositions: boolean[][] = (<FITHParsedGameData>this._gameData!).explodePositions,
            baseActiveRowsBefore: number = (<FITHParsedGameData>this._gameData!).baseActiveRowsBefore,
            baseActiveRowsAfter: number = (<FITHParsedGameData>this._gameData!).baseActiveRowsAfter,
            multiplier: number = this._gameData!.currentMultiplier,
            totalWin: number = this._gameData!.totalWin,
            delay: number = 0.2,
            wildExplodeIds: number[] = [];

        for (let i: number = 0; i < explodePositions.length; i++) {
            for (let j: number = 0; j < explodePositions[i].length; j++) {
                if (explodePositions[i][j]) {
                    if (Reels.getSymbol(i, j)!.symName === ReelSymbolName.WILD) {
                        wildExplodeIds.push(i, j);
                    }
                }
            }
        }

        for (let i: number = 0; i < this._betWinsData!.length; i++) {
            startTime += this.addSingleBetWinAnimationToTimeline(<IBetWayWinData>this._betWinsData![i], timeline, startTime);
        }

        wildFoundInExplode = wildExplodeIds.length > 0;
        if (wildFoundInExplode) {
            timeline.add(FITHGameModuleConfig.symbolWinAnimations.playWildExplodeAnimation(wildExplodeIds, allWinPositions, explodePositions, multiplier, winRatioConfig.type), startTime);
        } else {
            const soundDelay: number = 8 / 30;
            // #218
            if (allTogether) {
                timeline.add(() => FITHSoundManager.playSymbolsExplodeSmall(), startTime + soundDelay);
            }

            let prevReelId: number = -1;
            allWinPositions.forEach((pos: boolean[], reelId: number) => {
                pos.forEach((hasWin: boolean, symId: number) => {
                    if (hasWin) {
                        const timeOffset: number = (reelId * (delay - reelId * 0.01));
                        timeline.add(FITHGameModuleConfig.symbolWinAnimations.playWinAnimation(reelId, symId, winRatioConfig.type),
                            startTime + (allTogether ? 0 : timeOffset));

                        // #218
                        if (!allTogether) {
                            timeline.add(() => {
                                // added this check so that sound is played once for each reel that has winning sym on it.
                                if (prevReelId !== reelId) {
                                    prevReelId = reelId;

                                    if (!this._isAborted || !this._smallExplodeSoundPlayed) {
                                        this._smallExplodeSoundPlayed = true;
                                        FITHSoundManager.playSymbolsExplodeSmall();
                                    }
                                }
                            }, startTime + timeOffset + soundDelay);
                        }
                    }
                });
            });
        }

        const avalancheTimeline: TimelineLite = new TimelineLite({paused: true});
        let avalancheDelay: number = 0,
            canPlaySymLandingSound: boolean = false;

        explodePositions.forEach((pos: boolean[], reelId: number) => {
            const avalanchePositions: boolean[] = pos.concat();

            // mark all positions as true which do not match with allWinPositions.
            for (let i: number = 0; i < avalanchePositions.length; i++) {
                if (allWinPositions[reelId][i]) {
                    avalanchePositions[i] = true;
                }
            }
            // if there is X symbol in the coming active area's position.
            if (this._gameData!.reels[reelId][baseActiveRowsAfter - 1] === "X") {
                avalanchePositions[baseActiveRowsAfter - 1] = true;
            }

            // #209
            if (!canPlaySymLandingSound) {
                for (let i: number = 0; i < (baseActiveRowsAfter - 1); i++) {
                    if (!avalanchePositions[i] && avalanchePositions[i + 1]) {
                        canPlaySymLandingSound = true;

                        break;
                    }
                }
            }

            // mark all positions as false which were true due to explode in the inactive area.
            for (let i: number = baseActiveRowsAfter; i < avalanchePositions.length; i++) {
                if (avalanchePositions[i]) {
                    avalanchePositions[i] = false;
                }
            }

            avalancheTimeline.add(() => {
                let reel: FITHAvalancheReelController = (Reels.getReel(reelId) as FITHAvalancheReelController);
                reel.reMakeDAsBigSymbol();
            }, avalancheDelay);
            avalancheDelay = Reels.addReelAvalancheToTimeline(reelId, avalancheTimeline, avalancheDelay, reelId !== 0, avalanchePositions);
        });
        timeline.add(() => {
            avalancheTimeline.play();

            if (baseActiveRowsBefore !== baseActiveRowsAfter) {
                // #218
                FITHSoundManager.playGridDown();

                FITHGameModuleConfig.reelFrameView.moveBarrierDown((baseActiveRowsAfter - baseActiveRowsBefore));
            }

            SlotGame.winFieldController.showWinField(totalWin);

            // #209
            if (canPlaySymLandingSound) {
                FITHSoundManager.playSymLandingWood2();
            }
        });
    }

    protected addSymbolWinAnimationToTimeline(betWayWinData: IBetWayWinData, timeline: Timeline, startTime: number, symbol: StateReelSymbol, reelId: number, symbolId: number): number {
        timeline.add([
            () => {
                this.sortSymbolDepthToTop(symbol);

                // #218
                if (!this._betlineHighlightSoundPlayed) {
                    this._betlineHighlightSoundPlayed = true;
                    FITHSoundManager.playBetlineHighlight();
                }
            },
            FITHGameModuleConfig.symbolWinAnimations.playStrokeAnimation(reelId, symbolId),
            Reels.getSymbol(reelId, symbolId)!.changeState({
                state: FITHGameConfig.instance.SYMBOL_STATES.win,
                setStack: true,
                fadeInDuration: 0.1
            })
        ], startTime);

        // only play on the last reel top symbol.
        if ((reelId === betWayWinData.length - 1) && betWayWinData.reelWinPositions[reelId].indexOf(true) === symbolId) {
            // The win number animation will not be removed by abort and stop.
            const position: [number, number] = Helper.getSymbolPositions(reelId, symbolId, true),
                winNumberTimeline: TimelineLite = this._view.playWinNumbers(betWayWinData, position);
            this._winNumberTimelines.push(winNumberTimeline);
            timeline.add(() => winNumberTimeline.play(), startTime);
            (<TimelineLite>timeline).removeLabel("displayed"); // To remove if that's not the last one.
            (<TimelineLite>timeline).addLabel("displayed", startTime);
        }
        return 0;
    }

    protected addWinCountUpToTimeline(timeline: Timeline): void {
        const gameData: FITHParsedGameData = <FITHParsedGameData>this._gameData!;
        if (gameData.isEndBigWin) {
            // leave the ways won move down and remove itself.
            return;
        }

        const totalWin: number = gameData.avalancheTotalWin;
        const from: number = 0;
        const duration: number = gameData.currentAvalancheWinRatioConfig!.duration;
        const countUpTimeline: TimelineLite = new TimelineLite(),
            type: WinRatioType | undefined = gameData.currentAvalancheWinRatioConfig!.type;

        this._winWaysWon = (this._gameData! as FITHParsedGameData).waysWonTotalRound;
        countUpTimeline.add(this._winCountUp.getCountUpAnimation(from, totalWin, duration, type));
        countUpTimeline.add(() => SlotGame.winFieldController.showWinField(this._gameData!.totalWin));

        timeline.add([
            () => this._waysWon.showWinWaysWon(this._winWaysWon),
            countUpTimeline
        ], 0);
    }

    protected stopWinPresentation(isAborted: boolean): void {
        this._isAborted = isAborted;
        FITHGameModuleConfig.symbolWinAnimations.isAborted = isAborted;
        FITHGameModuleConfig.wildMultiplierView.isAborted = isAborted;

        if (this._timeline) {
            if (this._timeline.isActive()) {
                this._timeline.pause();
                this._timeline.kill();
            }
            this._timeline = null;
        }
        if (this._animationTimeline) {
            this._animationAborted = true;
            if (this._animationTimeline.progress() === 0) {
                this._animationTimeline.play();
                this._animationTimeline.progress(1);
                this._animationTimeline.kill();
            } else if (this._animationTimeline.isActive()) {
                this._animationTimeline.progress(1);
                this._animationTimeline.kill();
            }
            this._animationTimeline = null;
        }
        // Do no abort the avalanche timeline so it can be continued.
        FITHGameModuleConfig.symbolWinAnimations.reset();
        this._winSymbols = [];
        if (this._gameData!.nextMode !== FITHGameMode.NORMAL_AVALANCHE && !(this._betWinsData != null && this._betWinsData.length > 0)) {
            const gameData: FITHParsedGameData = (<FITHParsedGameData>this._gameData!);
            this._winCountUp.abort(gameData.avalancheTotalWin, isAborted, gameData.isBigWin);
            this._waysWon.abort(isAborted, false, this._winWaysWon);
        }

        // #218
        this._betlineHighlightSoundPlayed = false;
        this._smallExplodeSoundPlayed = false;
    }

    protected resetFeature() {
        super.resetFeature && super.resetFeature();

        this._winWaysWon = 0;

        // Only abort/stop win numbers when skip/spinStart/ScreenStart
        while (this._winNumberTimelines.length > 0) {
            let timeline: TimelineLite | null = this._winNumberTimelines.pop()!;
            if (timeline.progress() === 0) {
                timeline.play();
                timeline.progress(1);
                timeline.kill();
            } else if (timeline.isActive()) {
                timeline.progress(1);
                timeline.kill();
            }
            timeline = null;
        }
        this._animationAborted = false;
    }
}